import React, { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import { FaCheck, FaSearch } from 'react-icons/fa';
import { MdPhoneForwarded } from 'react-icons/md';
import { set_notification_variable as setNotificationVariable } from '../../../../redux/action_creators/home_actions';
import { CallbackHandler } from '../../../../myt_chat/chat/callbacks/handler';
import {
  cancelTransfer,
  fetchTransferByUUID,
  fetchTransferTargets,
  startTransfer,
  transferErrorReason
} from './transfer_api';
import type { TransferChannel, TransferStatus, TransferTarget } from './transfer_api';
import {
  TARGET_STATUS_LABEL,
  TRANSFER_NOTE_MAX,
  filterTargets,
  isFinalTransferStatus,
  isTargetSelectable,
  secondsLeft,
  transferButtonLabel,
  transferOutcomeMessage,
  transferPanelPlacement,
  transferRingingLabel
} from './transfer_state';
import type { PanelPlacement } from './transfer_state';
import '../twilio_agent_settings.css';
import './transfer.css';

// Grace after the ring deadline before asking the server, in case the relay was lost.
const STATUS_POLL_GRACE_MS = 5000;
// The server times a lost ring out 15 s after the deadline, so keep asking a little past that.
const STATUS_POLL_MAX_TRIES = 6;

interface Ringing {
  transferUUID: string;
  toName: string;
  expiresAt: number;
  channel?: TransferChannel;
}

interface TransferControlProps {
  callUUID: string | null;
}

// The call pill is the nearest fixed-position ancestor of the Transfer button.
const fixedAncestor = (el: HTMLElement | null): HTMLElement | null => {
  for (let node = el?.parentElement; node; node = node.parentElement) {
    if (getComputedStyle(node).position === 'fixed') return node;
  }
  return null;
};

interface RelayItem {
  data?: { type?: string; transfer_uuid?: string; status?: TransferStatus; to_agent_name?: string };
}

/** A's Transfer button on the call pill: pick a colleague, ring them, follow the outcome. */
export default function TransferControl({ callUUID }: TransferControlProps) {
  const dispatch = useDispatch();
  const [open, setOpen] = useState(false);
  const [targets, setTargets] = useState<TransferTarget[]>([]);
  const [loading, setLoading] = useState(false);
  const [query, setQuery] = useState('');
  const [selected, setSelected] = useState<TransferTarget | null>(null);
  const [note, setNote] = useState('');
  const [sending, setSending] = useState(false);
  const [ringing, setRinging] = useState<Ringing | null>(null);
  const [joinedName, setJoinedName] = useState<string | null>(null);
  const [now, setNow] = useState(() => Date.now());
  const [placement, setPlacement] = useState<PanelPlacement | null>(null);
  const ringingRef = useRef<Ringing | null>(null);
  ringingRef.current = ringing;
  // Outcomes that arrived before POST /transfer answered, keyed by transfer uuid.
  const earlyOutcomesRef = useRef<Record<string, { status: TransferStatus; toName: string }>>({});
  const triggerRef = useRef<HTMLButtonElement | null>(null);
  const searchRef = useRef<HTMLInputElement | null>(null);

  const notify = useCallback(
    (type: 'error' | 'success', message: string) =>
      dispatch(setNotificationVariable(true, type, message)),
    [dispatch]
  );

  const applyStatus = useCallback(
    (status: TransferStatus, toName: string) => {
      if (status === 'ringing') return;
      setRinging(null);
      if (status === 'accepted' || status === 'completed') {
        setJoinedName(toName || 'Your colleague');
        return;
      }
      const message = transferOutcomeMessage(status, toName);
      if (message) notify('error', message);
    },
    [notify]
  );

  useEffect(() => {
    const handler = (item: RelayItem) => {
      const data = item?.data;
      if (!data || data.type !== 'transfer_updated' || !data.status) return;
      const { current } = ringingRef;
      // Only this pill's transfer; 'returned' arrives after ringing has already cleared.
      if (current && current.transferUUID !== data.transfer_uuid) return;
      if (!current && data.status !== 'returned') {
        if (data.transfer_uuid) {
          earlyOutcomesRef.current[data.transfer_uuid] = {
            status: data.status,
            toName: data.to_agent_name || ''
          };
        }
        return;
      }
      applyStatus(data.status, data.to_agent_name || current?.toName || '');
      if (data.status === 'returned') setJoinedName(null);
    };
    CallbackHandler.shared.registerRelayItemListener(handler);
    return () => CallbackHandler.shared.unregisterRelayItemListener(handler);
  }, [applyStatus]);

  useEffect(() => {
    if (!ringing) return undefined;
    const id = window.setInterval(() => setNow(Date.now()), 1000);
    return () => window.clearInterval(id);
  }, [ringing]);

  // Self-heal a lost relay: past the deadline, ask the server until it settles the ring.
  useEffect(() => {
    if (!ringing) return undefined;
    let timer: number | undefined;
    let tries = 0;
    const check = () => {
      fetchTransferByUUID(ringing.transferUUID)
        .then((t) => {
          if (ringingRef.current?.transferUUID !== t.transfer_uuid) return;
          if (t.status === 'accepted' || isFinalTransferStatus(t.status)) {
            applyStatus(t.status, t.to_agent_name);
            return;
          }
          tries += 1;
          if (tries < STATUS_POLL_MAX_TRIES) timer = window.setTimeout(check, STATUS_POLL_GRACE_MS);
        })
        .catch(() => setRinging(null));
    };
    timer = window.setTimeout(
      check,
      Math.max(0, ringing.expiresAt * 1000 + STATUS_POLL_GRACE_MS - Date.now())
    );
    return () => window.clearTimeout(timer);
  }, [ringing, applyStatus]);

  // Place the panel from the pill's real size before paint, and again whenever the window changes.
  useLayoutEffect(() => {
    if (!open) return undefined;
    const place = () => {
      const pill = fixedAncestor(triggerRef.current)?.getBoundingClientRect() ?? null;
      setPlacement(
        transferPanelPlacement(pill, { width: window.innerWidth, height: window.innerHeight })
      );
    };
    place();
    window.addEventListener('resize', place);
    return () => window.removeEventListener('resize', place);
  }, [open]);

  useEffect(() => {
    if (!open) return undefined;
    // On phones the keyboard would cover the sheet, so only desktop jumps straight to search.
    if (placement?.mode === 'anchored') searchRef.current?.focus();
    const onKey = (e: KeyboardEvent) => {
      if (e.key === 'Escape') setOpen(false);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [open, placement?.mode]);

  const openPicker = () => {
    if (!callUUID) return;
    setOpen(true);
    setSelected(null);
    setQuery('');
    setNote('');
    setLoading(true);
    fetchTransferTargets(callUUID)
      .then(setTargets)
      .catch((err) => {
        notify('error', transferErrorReason(err, 'Could not load colleagues. Try again.'));
        setOpen(false);
      })
      .finally(() => setLoading(false));
  };

  const send = () => {
    if (!callUUID || !selected || sending) return;
    setSending(true);
    earlyOutcomesRef.current = {};
    startTransfer(callUUID, selected.uuid, note.trim())
      .then((started) => {
        setOpen(false);
        const early = earlyOutcomesRef.current[started.transfer_uuid];
        earlyOutcomesRef.current = {};
        if (early) {
          applyStatus(early.status, early.toName || selected.name);
          return;
        }
        setRinging({
          transferUUID: started.transfer_uuid,
          toName: selected.name,
          expiresAt: started.expires_at,
          channel: started.channel
        });
        setNow(Date.now());
      })
      .catch((err) => notify('error', transferErrorReason(err, 'Could not start the transfer.')))
      .finally(() => setSending(false));
  };

  const cancel = () => {
    const current = ringing;
    if (!current) return;
    cancelTransfer(current.transferUUID)
      .then((status) => {
        if (status === 'cancelled') setRinging(null);
        else applyStatus(status, current.toName);
      })
      .catch((err) => notify('error', transferErrorReason(err, 'Could not cancel the transfer.')));
  };

  const visible = useMemo(() => filterTargets(targets, query), [targets, query]);

  if (!callUUID) return null;

  if (joinedName) {
    return <span className="tc-status">{joinedName} joined</span>;
  }

  if (ringing) {
    return (
      <span className="tc-status">
        {`${transferRingingLabel(ringing.toName, ringing.channel)} · ${secondsLeft(ringing.expiresAt, now)}s`}
        <button type="button" className="tc-link" onClick={cancel}>
          Cancel
        </button>
      </span>
    );
  }

  return (
    <>
      <button
        ref={triggerRef}
        type="button"
        className="tc-trigger"
        onClick={openPicker}
        aria-haspopup="dialog"
        aria-expanded={open}
        aria-label="Transfer call"
        title="Transfer this call to a colleague"
      >
        <MdPhoneForwarded size={16} />
      </button>
      {open &&
        placement &&
        createPortal(
          <div
            className={`tc-panel tc-panel-${placement.mode}`}
            style={{ top: placement.top, right: placement.right, maxHeight: placement.maxHeight }}
            role="dialog"
            aria-label="Transfer call"
          >
            <div className="tc-title">Transfer to a colleague</div>
            <div className="ta-dropdown-search">
              <FaSearch size={12} />
              <input
                ref={searchRef}
                type="text"
                className="ta-dropdown-search-input"
                placeholder="Search by name or email"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                aria-label="Search colleagues"
              />
            </div>
            <div className="ta-dropdown-options tc-options" role="listbox">
              {loading && <div className="ta-dropdown-empty">Loading…</div>}
              {!loading && visible.length === 0 && (
                <div className="ta-dropdown-empty">No colleagues found</div>
              )}
              {!loading &&
                visible.map((t) => {
                  const isSelected = selected?.uuid === t.uuid;
                  return (
                    <button
                      key={t.uuid}
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      disabled={!isTargetSelectable(t)}
                      className={`ta-dropdown-option tc-option${isSelected ? ' tc-option-selected' : ''}`}
                      onClick={() => setSelected(t)}
                    >
                      <span className="tc-check" aria-hidden="true">
                        <FaCheck size={11} />
                      </span>
                      <span className="ta-dropdown-option-name tc-option-name">
                        {t.name || t.email}
                      </span>
                      <span className={`tc-dot-label tc-dot-${t.status}`}>
                        {TARGET_STATUS_LABEL[t.status]}
                      </span>
                    </button>
                  );
                })}
            </div>
            <textarea
              className="tc-note"
              rows={2}
              maxLength={TRANSFER_NOTE_MAX}
              placeholder="Reason for the transfer (optional)"
              value={note}
              onChange={(e) => setNote(e.target.value)}
              aria-label="Transfer note"
            />
            <div className="tc-actions">
              <button type="button" className="tc-secondary" onClick={() => setOpen(false)}>
                Cancel
              </button>
              <button
                type="button"
                className="tc-primary"
                onClick={send}
                disabled={!selected || sending}
              >
                {sending ? 'Sending…' : transferButtonLabel(selected?.name)}
              </button>
            </div>
          </div>,
          document.body
        )}
    </>
  );
}
